import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';

import Auth, { type LoginResponse } from 'src/services/api/auth/Auth';
import Checkbox from 'src/components/input/checkbox/Checkbox';
import Logo from 'src/components/logo/Logo';
import MaintenanceWindowLoginAlert from 'src/components/alerts/MaintenanceWindowLoginAlert';
import PageTitle from 'src/components/page-title/PageTitle';
import PasswordField from 'src/components/input/password/PasswordField';
import TextField from 'src/components/input/text-field/TextField';
import { Config } from 'src/config';
import { Flex, FlexColumn } from 'src/components/flex';
import { LANDLORD_HOME_PATH } from 'src/routes';
import { ThemeProvider } from 'src/theme';
import { isHTTPError } from 'src/common/api-utils';
import { type IUser } from 'src/api/users-api';
import { useLoggedInUser, useLogin } from 'src/services/api';
import { usePersistentState } from 'src/services/storage/use-persistent-state';
import { LandlordTeamSelectPaper } from 'src/components/layout/landlord/LandlordTeamSelect';
import ServerError from 'src/components/errors/ServerError';

import styles from 'src/pages/landlord/login/LandlordLogin.module.css';

enum LoginStep {
    LOGIN,
    ORGANIZATION_SELECT,
}

export type TempLoginData = LoginResponse;

export default function LandlordLoginPage() {
    const { data: loggedInUser, error: loggedInUserError } = useLoggedInUser();
    const { mutateAsync: login, isLoading } = useLogin(false);
    const [loginStep, setLoginStep] = useState(LoginStep.LOGIN);
    const [tempLoginData, setTempLoginData] = useState<TempLoginData | undefined>(undefined);

    const [email, setEmail, deleteEmail] = usePersistentState(
        'userEmail',
        ''
    );
    const [password, setPassword] = useState('');
    const [isRememberMe, setIsRememberMe, deleteIsRememberMe] = usePersistentState(
        'isRememberMe',
        false
    );
    const [error, setError] = useState<string>();
    const [isServerError, setIsServerError] = useState(false);

    const [, , deleteInactivePropertiesClosed] = usePersistentState(
        'inactivePropertiesClosed',
        false
    );

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(undefined);
        setIsServerError(false);

        if (isRememberMe) {
            setEmail(email);
            setIsRememberMe(true);
        } else {
            deleteEmail();
            deleteIsRememberMe();
        }

        try {
            const { user, access_token, refresh_token } = await login({ email, password });
            deleteInactivePropertiesClosed();
            // NOTE: we save login info BEFORE team selection - allows us to get available teams for current user
            Auth.saveLoginInfo({ access_token, refresh_token, user });
            if (user.org_user_id) {
                setLoginStep(LoginStep.ORGANIZATION_SELECT);
                setTempLoginData({ user, access_token, refresh_token });
            } else {
                redirectToNextPath(user);
            }
        } catch (_error) {
            if (isHTTPError(_error)) {
                if (_error.response.status >= 500 && _error.response.status < 600) {
                    setIsServerError(true);
                } else {
                    const res: any = await _error.response.json();
                    if (res.error_code === 'VALIDATION_ERROR') {
                        setError('Invalid email or password');
                    } else {
                        setError(res.msg);
                    }
                }
            }
        }
    };

    const handleChange = (field: 'email' | 'password', value: string) => {
        switch (field) {
            case 'email': {
                setEmail(value);
                break;
            }
            case 'password': {
                setPassword(value);
                break;
            }
        }
    };

    if (Auth.accessToken && !loggedInUserError && loginStep === LoginStep.LOGIN) {
        if (!loggedInUser) {
            return <LinearProgress />;
        } if (!loggedInUser?.is_onboarding_complete) {
            return <Navigate to="/landlord/onboard" replace />;
        } else {
            return <Navigate to={LANDLORD_HOME_PATH} replace />;
        }
    }

    const loginStepForm = (
        <form onSubmit={handleSubmit}>
            {isServerError && <ServerError onDismiss={() => setIsServerError(false)} />}
            {error && <Alert severity="error" data-testid="login-error-alert">{error}</Alert>}

            <TextField
                type="email"
                name="email"
                placeholder="Email"
                value={email}
                onChange={(value) => handleChange('email', value)}
                autoComplete="email"
                required
                fullWidth
                data-testid="login-email-input"
            />

            <PasswordField
                value={password}
                onChange={(event) => handleChange('password', event.target.value)}
                autoComplete="current-password"
                data-testid="login-password-input"
            />

            <Flex
                justifyContent="space-between"
                mobileFlexDirection="column"
                gap={2}
                mt={1}
            >
                <Typography variant="body1" color="grey.700" alignContent="center">
                    <Link to="/landlord/password-reset/start" data-testid="reset-password-link">Forgot password?</Link>
                </Typography>

                <Checkbox
                    label="Remember me on this device"
                    value={isRememberMe}
                    onChange={() => setIsRememberMe(!isRememberMe)}
                />
            </Flex>

            <LoadingButton type="submit" variant="contained" loading={isLoading} fullWidth size="large" data-testid="signin-button">
                Sign in
            </LoadingButton>

        </form>
    );

    const organizationSelectForm = <LandlordTeamSelectPaper loginData={tempLoginData} />;

    const loginComponent = (
        <FlexColumn component={Paper} className={styles.paper}>
            <Logo />
            {loginStep === LoginStep.LOGIN && loginStepForm}
            {loginStep === LoginStep.ORGANIZATION_SELECT && organizationSelectForm}
        </FlexColumn>
    );

    return (
        <ThemeProvider>
            <PageTitle title="Reffie | Login">
                <div className={styles.root}>
                    <FlexColumn px={0} mx={0} className={styles.paperContainer}>
                        <MaintenanceWindowLoginAlert maintenanceWindow={Config.maintenanceWindow} className={styles.systemAlert} />
                        {!(Config.maintenanceWindow?.isActive()) && loginComponent}
                    </FlexColumn>
                </div>
            </PageTitle>
        </ThemeProvider>
    );
}

export const redirectToNextPath = (user: IUser) => {
    const url = new URL(window.location.href);
    const nextPath = url.searchParams.get('next_path');

    if (nextPath) {
        const nextUrl = new URL(nextPath, window.location.href);
        const searchParams = nextUrl.searchParams;
        searchParams.forEach((value, key) => {
            url.searchParams.set(key, value);
        });
        url.pathname = nextUrl.pathname;
        url.searchParams.delete('next_path');
    } else if (!user.is_onboarding_complete) {
        // onboarding is not complete for this user
        // redirect to the first step after personal profile which should be importing properties
        url.pathname = '/landlord/onboard';
    } else {
        url.pathname = LANDLORD_HOME_PATH;
    }

    window.location.assign(url.toString());
};
